import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  fetchDialogs,
  createDialog,
  updateDialog,
  deleteDialog,
  getCachedDialogs,
  getCachedDialogsByFolder,
  dialogStore,
} from '@/lib/stores/dialogStore';
import { useToast } from '@/hooks/use-toast';
import { useFolderContext } from './FolderProvider';
import { useSimplifiedUserData } from './SimplifiedUserDataProvider';

interface DialogDataContextType {
  dialogs: Dialog[];
  isLoading: boolean;
  loadData: () => Promise<void>;
  refreshDialogs: (gameId: string) => Promise<void>;
  createDialogItem: (dialog: any) => Promise<Dialog>;
  updateDialogItem: (dialog: any) => Promise<Dialog>;
  deleteDialogItem: (id: string, gameId: string) => Promise<void>;
}

const DialogDataContext = createContext<DialogDataContextType | undefined>(undefined);

export const DialogDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { toast } = useToast();
  const { selectedGame } = useSimplifiedUserData();
  const [dialogs, setDialogs] = useState<Dialog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const { selectedFolderId } = useFolderContext();

  // Load data function that can be called from pages
  const loadData = useCallback(async () => {
    if (!selectedGame?.id) return;

    setIsLoading(true);
    try {
      const fetchedDialogs = await fetchDialogs(selectedGame.id, undefined, toast);
      setDialogs(fetchedDialogs);
    } catch (error) {
      console.error('Error loading dialogs:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedGame?.id, toast]);

  // Load cached data immediately when game changes, then fetch fresh data
  useEffect(() => {
    if (!selectedGame?.id) return;

    // Load cached data immediately for instant response
    const cachedDialogs = getCachedDialogs(selectedGame.id);
    if (cachedDialogs.length > 0) {
      setDialogs(cachedDialogs);
    }
    
    // Always fetch fresh data when not initialized
    if (!isInitialized) {
      loadData();
      setIsInitialized(true);
    }
  }, [selectedGame?.id, isInitialized, loadData]);

  // Reset initialization when selected game changes
  useEffect(() => {
    setIsInitialized(false);
  }, [selectedGame?.id]);

  // Subscribe to store changes for real-time updates
  useEffect(() => {
    if (!selectedGame?.id) return;

    const unsubscribe = dialogStore.subscribe(() => {
      const allDialogs = dialogStore.getData();
      const gameDialogs = allDialogs.filter(dialog => dialog.game_id === selectedGame.id);
      setDialogs(gameDialogs);
    });

    return unsubscribe;
  }, [selectedGame?.id]);

  const refreshDialogs = useCallback(async (gameId: string) => {
    setIsLoading(true);
    try {
      await fetchDialogs(gameId, selectedFolderId || undefined, toast);
    } catch (error) {
      console.error('Error refreshing dialogs:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedFolderId, toast]);

  const createDialogItem = useCallback(async (dialog: any) => {
    return await createDialog(dialog, toast);
  }, [toast]);

  const updateDialogItem = useCallback(async (dialog: any) => {
    return await updateDialog(dialog, toast);
  }, [toast]);

  const deleteDialogItem = useCallback(async (id: string, gameId: string) => {
    return await deleteDialog(id, gameId, toast);
  }, [toast]);

  const value: DialogDataContextType = {
    dialogs,
    isLoading,
    loadData,
    refreshDialogs,
    createDialogItem,
    updateDialogItem,
    deleteDialogItem,
  };

  return <DialogDataContext.Provider value={value}>{children}</DialogDataContext.Provider>;
};

export const useDialogDataContext = () => {
  const context = useContext(DialogDataContext);
  if (!context) {
    throw new Error('useDialogDataContext must be used within a DialogDataProvider');
  }
  return context;
};

// Optional hook that returns null if not in context (for use in components that may be outside the provider)
export const useDialogDataContextOptional = () => {
  return useContext(DialogDataContext);
};

